import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { PCrmInvoiceCategory } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Invoice Categories',
    description: 'Manage invoice categories',
    permissionModuleKey: 'invoice_category',
    route: {
      list: '/p_crm_invoice_category',
      new: '/p_crm_invoice_category/new',
      detail: (id: number) => `/p_crm_invoice_category/${id}`,
      edit: (id: number) => `/p_crm_invoice_category/${id}/edit`,
    },
    api: {
      list: '/p-crm-invoice-category',
      detail: (id: number) => `/p-crm-invoice-category/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search invoice categories...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['pic_descr'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'pic_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: PCrmInvoiceCategory) => item.pic_descr,
        options: [],
      },
    ],
    applyFilter: (items: PCrmInvoiceCategory[], filters: { pic_descr: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.pic_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.pic_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.pic_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Invoice Categorie',
        showTitle: false,
        variant: 'primary' as const,
        href: '/p_crm_invoice_category/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: PCrmInvoiceCategory) => {
      window.location.href = `/p_crm_invoice_category/${item.id}`;
    },
    keyExtractor: (item: PCrmInvoiceCategory) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new invoice categorie.',
    noSearchResults: 'No invoice categories found matching your search criteria.',
    noFilterResults: 'No invoice categories match the selected filters.',
  },
  delete: {
    title: 'Delete Invoice Categorie',
    message: 'Are you sure you want to delete this invoice categorie? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete invoice categorie. Please try again.',
    handler: async (id: number) => {
      const { deleteInvoiceCategory } = await import('../../../services/mutations');
      await deleteInvoiceCategory(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Invoice Categorie Details',
    notFoundTitle: 'Invoice Categorie not found',
    notFoundMessage: 'The invoice categorie you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Invoice Categories',
    defaultTitle: 'Unnamed Invoice Categorie',
    getTitle: (item: PCrmInvoiceCategory) => {
      return item.pic_descr || `Invoice Categorie #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Invoice Categorie',
      subtitle: 'Add a new invoice categorie',
      submitText: 'Create Invoice Categorie',
    },
    edit: {
      title: 'Edit Invoice Categorie',
      subtitle: 'Update invoice categorie information',
      submitText: 'Update Invoice Categorie',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading invoice categorie...',
    errorMessage: 'Failed to save invoice categorie. Please try again.',
    create: async (data: Partial<PCrmInvoiceCategory>) => {
      const { createInvoiceCategory } = await import('../../../services/mutations');
      return await createInvoiceCategory(data);
    },
    update: async (id: number, data: Partial<PCrmInvoiceCategory>) => {
      const { updateInvoiceCategory } = await import('../../../services/mutations');
      return await updateInvoiceCategory(id, data);
    },
  },
  fields: [
    {
      name: 'pic_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter invoice categorie description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Invoice Categorie Information',
        icon: '📝',
        fields: ['pic_descr'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<PCrmInvoiceCategory>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'pic_descr',
    header: 'Description',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: PCrmInvoiceCategory,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.pic_descr || '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: PCrmInvoiceCategory) => {
  return null;
};

export const getInitialFormData = (): Partial<PCrmInvoiceCategory> => {
  return {
    pic_descr: '',
  };
};

export const mapItemToFormData = (item: PCrmInvoiceCategory): Partial<PCrmInvoiceCategory> => {
  return {
    pic_descr: item.pic_descr || '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: PCrmInvoiceCategory,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

